'use client'

import { Button } from '@/components/ui/button'
import { Check } from 'lucide-react'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { GeniiusUser } from '@/lib/types'
import CheckoutForm from '@/components/stripe/CheckoutForm'
import { useRouter } from 'next/navigation'
import { useState, useEffect } from 'react'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { ContactSalesForm } from '@/components/forms/contact-sales-form'
import { useLogger } from '@/lib/hooks/useLogger';
import { getPlanFromSubscription } from '@/lib/plan-manager/pricing-plans';
import { nanoid } from 'nanoid'
import { toast } from '@/lib/toast'
import { createOrganizationBillingPortal } from '@/app/actions/stripe'

export interface PricingPlan {
  name: string
  price: string
  description: string
  features: string[]
  cta: string
  footnote?: string
  productId?: string
}

export interface PricingCardsProps {
  showCheckout?: boolean
  user?: GeniiusUser | null
  organization?: {
    subscription_details?: {
      subscription_id: string | null;
      product_id: string;
      is_active: boolean;
      createdAt: Date;
      maxUsersAllowedInOrg?: number;
    };
    stripe_customer_id?: string;
    name: string;
    id: string;
    membersCount?: number;
  } | null;
  isLanding?: boolean;
  orgId?: string | null; // Add Clerk organization ID
  isAdmin?: boolean;
}

export const personalPlans: PricingPlan[] = [
  {
    name: "Free",
    price: "$0",
    description: "",
    productId: "",
    features: [
      "Chat with AI from ChatGPT, Claude, Perplexity, and Gemini",
      "Private group chats with AI and up to 3 people",
      "Send up to 20 AI messages per day",
      "Unlimited messages to people, comments, replies, and upvotes",
      "Unlimited file uploads",
      "30-day free access to create or join communities (user-created groups with private chat feeds)"
    ],
    cta: "Get started"
  },
  {
    name: "Premium",
    price: "$20",
    description: "",
    productId: process.env.NEXT_PUBLIC_STRIPE_PREMIUM_PRODUCT_ID,
    features: [
      "Everything in Free, plus:",
      "Private group chats with AI and an unlimited number of people",
      "Send up to 250 AI messages per day",
      "See full names of people who viewed your profile",
      "Your name is not displayed when viewing others' profiles",
      "Unlimited access to create or join communities (user-created groups with private chat feeds)"
    ],
    cta: "Upgrade now"
  }
]

export const businessPlans: PricingPlan[] = [
  {
    name: "Team",
    price: "$150",
    description: "",
    features: [
      "Everything in Premium, plus:",
      "Up to 500 AI messages per day per user",
      "Private organization feed with team-only content",
      "Centralized billing",
      "Role-based access control",
      "Priority support",
      "Early access to collaboration features",
      "High-speed access with priority bandwidth",
      "Advanced administration and user management"
    ],
    cta: "Contact sales",
  },
  {
    name: "Enterprise",
    price: "Custom",
    description: "",
    features: [
      "Everything in Team, plus:",
      "Private database for your organization",
      "Custom message limits",
      "Create department-based organizations",
      "Expanded context window for AI interactions",
      "Single sign-on (SSO) and domain control",
      "Audit logs and analytics",
      "Advanced administration and user management",
      "Custom security and compliance controls",
      "Dedicated implementation and support team"
    ],
    cta: "Contact sales"
  }
]

// New plans specifically for subscription dialog
export const subscriptionPlans: PricingPlan[] = [
  {
    name: "Mixus Business",
    price: "$20",
    description: "Perfect for growing teams and organizations",
    productId: process.env.NEXT_PUBLIC_STRIPE_MIXUS_BUSINESS_PRODUCT_ID,
    features: [
      "Unlimited AI conversations",
      "Expert colleague recommendations",
      "Team collaboration features",
      "Knowledge management",
      "SSO & SAML",
    ],
    cta: "Subscribe",
  },
  {
    name: "Mixus Enterprise",
    price: "Custom",
    description: "Self-hosted solution with maximum control",
    features: [
      "Everything in Business, plus:",
      "Self-hosted deployment",
      "Custom AI model integration",
      "Dedicated support team",
      "Custom integrations"
    ],
    cta: "Let's Talk"
  }
]

export function PricingCards({ showCheckout, user, organization, isLanding, orgId, isAdmin }: PricingCardsProps) {
  // Use subscription plans if showing in subscription dialog
  const plansToShow = isLanding ? personalPlans : subscriptionPlans;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
      {plansToShow.map((plan, index) => (
        <PricingCard
          key={index}
          plan={plan}
          showCheckout={showCheckout}
          user={user}
          organization={organization}
          isLanding={isLanding}
          orgId={orgId}
          isAdmin={isAdmin}
        />
      ))}
    </div>
  )
}

const PricingCard = ({ plan, showCheckout: initialShowCheckout, user, organization, isLanding, orgId, isAdmin }: {
  plan: PricingPlan
  showCheckout?: boolean
  user?: GeniiusUser | null
  organization?: PricingCardsProps['organization']
  isLanding?: boolean
  orgId?: string | null
  isAdmin?: boolean
}) => {
  const router = useRouter()
  const [showContactForm, setShowContactForm] = useState(false)
  const [showCheckout, setShowCheckout] = useState(initialShowCheckout)
  const logger = useLogger()
  const [selectedProductId, setSelectedProductId] = useState(process.env.NEXT_PUBLIC_STRIPE_TEAM10_PRODUCT_ID)
  const [currentPrice, setCurrentPrice] = useState("$150")

  // Check if this is the current plan for the organization
  const isCurrentPlan = (() => {
    if (!organization?.subscription_details?.is_active) return false;

    if (plan.name === 'Mixus Business') {
      return organization.subscription_details.product_id === process.env.NEXT_PUBLIC_STRIPE_MIXUS_BUSINESS_PRODUCT_ID;
    }

    if (plan.name === 'Team') {
      return organization.subscription_details.product_id === selectedProductId;
    }

    return false;
  })();

  useEffect(() => {
    if (plan.name === 'Team') {
      switch (selectedProductId) {
        case process.env.NEXT_PUBLIC_STRIPE_TEAM10_PRODUCT_ID:
          setCurrentPrice("$150")
          break
        case process.env.NEXT_PUBLIC_STRIPE_TEAM50_PRODUCT_ID:
          setCurrentPrice("$400")
          break
        case process.env.NEXT_PUBLIC_STRIPE_TEAM100_PRODUCT_ID:
          setCurrentPrice("$600")
          break
        default:
          setCurrentPrice(plan.price)
      }
    }
  }, [selectedProductId, plan.name, plan.price])

  const buttonText = (() => {
    if (plan.name === 'Mixus Enterprise') return "Let's Talk"
    if (plan.name === 'Free') return !organization?.subscription_details?.is_active ? 'Current Plan' : 'Get Started'
    if (!isAdmin) return 'Contact Admin'
    if (!orgId) return 'Create Organization First'
    if (!organization) return 'Contact Admin'
    // Only check subscription_id for "Manage Subscription"
    if (organization.subscription_details?.subscription_id) return 'Manage Subscription'
    return 'Subscribe'
  })()

  const handleAction = async () => {
    logger.debug('handleAction', { plan: plan.name, isLanding })

    if (!isAdmin) {
      toast.error('Please contact your organization administrator to manage subscription')
      return
    }

    if (plan.name === 'Mixus Enterprise') {
      window.open('https://calendly.com/shai-mixus/30min', '_blank')
      return
    }

    // If user is not logged in on landing page
    if (isLanding) {
      switch (plan.name) {
        case 'Free':
          router.push('/sign-up')
          break
        case 'Mixus Business':
          router.push('/sign-up?redirect=/pricing?plan=business')
          break
        case 'Team':
          setShowContactForm(true)
          break
        default:
          logger.error('Unknown plan type:', plan.name)
      }
      return
    }

    // For logged in users
    if (!orgId) {
      router.push('/settings/organization/new')
      return
    }

    // If organization exists in Clerk but not in MongoDB, only admins can subscribe
    if (!organization) {
      return // Button will show "Contact Admin"
    }

    // Only check subscription_id for navigation
    if (organization.subscription_details?.subscription_id) {
      try {
        const { url } = await createOrganizationBillingPortal(orgId)
        if (url) {
          window.location.href = url
        } else {
          toast.error('Unable to access billing portal')
        }
      } catch (error) {
        logger.error('Failed to open billing portal:', error)
        toast.error('Failed to open billing portal')
      }
    } else {
      setShowCheckout(true)
    }
  }

  const cardClasses = (plan: PricingPlan) => {
    if (plan.name === 'Mixus Enterprise') {
      return 'relative rounded-xl p-8 h-full flex flex-col bg-black text-white border border-gray-800'
    }
    return 'relative rounded-xl p-8 h-full flex flex-col bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700'
  }

  return (
    <>
      <div className={cardClasses(plan)}>
        {isCurrentPlan && (
          <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
            <span className="bg-green-500 text-white px-3 py-1 rounded-full text-sm">
              Current Plan
            </span>
          </div>
        )}
        <div className="mb-8">
          <h3 className="text-2xl font-semibold mb-2">{plan.name}</h3>
          <div className="flex items-baseline gap-2">
            <span className="text-4xl font-bold tracking-tight">
              {plan.name === 'Team' ? currentPrice : plan.price}
            </span>
            {plan.name === 'Mixus Business' ? (
              <span className="text-gray-500 dark:text-gray-400">per user/month</span>
            ) : plan.name !== 'Mixus Enterprise' && (
              <span className="text-gray-500 dark:text-gray-400">per month</span>
            )}
          </div>
          <p className="text-lg text-gray-600 dark:text-gray-400 mt-4">
            {plan.description}
          </p>
        </div>

        {plan.name === 'Team' && (
          <select
            value={selectedProductId}
            onChange={(e) => setSelectedProductId(e.target.value)}
            className="mb-4 p-2 border rounded"
          >
            <option value={process.env.NEXT_PUBLIC_STRIPE_TEAM10_PRODUCT_ID}>Up to 10 members</option>
            <option value={process.env.NEXT_PUBLIC_STRIPE_TEAM50_PRODUCT_ID}>Up to 50 members</option>
            <option value={process.env.NEXT_PUBLIC_STRIPE_TEAM100_PRODUCT_ID}>Up to 100 members</option>
          </select>
        )}

        <ul className="space-y-4 mb-8 flex-grow">
          {plan.features.map((feature, idx) => (
            <li key={idx} className="flex items-start gap-3">
              <Check className={`w-5 h-5 shrink-0 mt-0.5 ${plan.name === 'Mixus Enterprise' ? 'text-white' : 'text-gray-500'}`} />
              <span className={plan.name === 'Mixus Enterprise' ? 'text-gray-300' : 'text-gray-600 dark:text-gray-300'}>
                {feature}
              </span>
            </li>
          ))}
        </ul>

        <div>
          {((showCheckout || initialShowCheckout) && user && plan.name !== 'Mixus Enterprise' && orgId) ? (
            <CheckoutForm
              uiMode="hosted"
              productId={plan.name === 'Team' && selectedProductId
                ? selectedProductId
                : plan.productId ?? ""}
              user={user}
              is_active={!!organization?.subscription_details?.subscription_id}
              is_free_plan={plan.productId === ""}
              team_id={orgId}
              is_admin={isAdmin ?? false}
              quantity={Math.max(organization?.membersCount || 5, 5)}
            />
          ) : (
            <Button
              className={`w-full py-6 text-base transition-all duration-200 ${
                plan.name === 'Mixus Enterprise' 
                  ? 'bg-white text-black hover:bg-gray-100' 
                  : 'bg-blue-500 hover:bg-blue-600 text-white'
              }`}
              onClick={handleAction}
              disabled={plan.name === 'Free' && !isLanding || !orgId}
            >
              {!orgId ? 'Create Organization First' : buttonText}
            </Button>
          )}
          {plan.footnote && (
            <p className="text-sm mt-4 text-gray-500">{plan.footnote}</p>
          )}
        </div>
      </div>

      {showContactForm && (
        <Dialog open={showContactForm} onOpenChange={setShowContactForm}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Contact Sales</DialogTitle>
              <DialogDescription>
                Tell us about your organization and we&apos;ll get back to you shortly.
              </DialogDescription>
            </DialogHeader>
            <ContactSalesForm
              planType={plan.name === 'Team' ? 'team' : 'enterprise'}
              onSuccess={() => setShowContactForm(false)}
            />
          </DialogContent>
        </Dialog>
      )}
    </>
  )
}
